import { HttpResponseBase } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Router, RouterStateSnapshot } from '@angular/router';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, concatMap, map } from 'rxjs/operators';
import { Client, ClientSetup } from '../models/client';
import { CompanyEarningConfig } from '../models/company-earning-config';
import { CompanyLocationConfig } from '../models/company-location-config';
import { ErrorDetail, WarningLevel } from '../models/error-detail';
import { LaborAllocationTypes } from '../models/labor-allocation';
import {
  AddPaylineRequestModel,
  CreatePayrollArgs,
  EmployeePay,
  LaborAllocation,
  MasterPayrollObject,
  PayDateInfo,
  PayGridSessionData,
  Payline,
  PaylineType,
  PayrollConfig,
  PayrollConfigSetupData,
  PayrollInputSources,
  PayrollStartConfig,
  PayrollStartData,
  PayrollStatuses,
  PayTypes,
  Schedule,
  ScheduleRequest
} from '../models/payroll';
import { PayrollFrequency } from '../models/payroll-info';
import { AuditService } from '../services/audit.service';
import { AuthService } from '../services/auth.service';
import { CompaniesService } from '../services/companies.service';
import { PayrollGridComponentConfig } from '../services/payroll-grid-component-config';
import { PayrollService } from '../services/payroll.service';
import {
  addNewPersonRouteRegex,
  payrollGridRouteRegex,
  payrollPreviewRouteRegex
} from '../utils/regex-patterns';
import { IPayrollEventData } from '../models/audit-action';
import { DatePipe } from '@angular/common';
import { InstanceTypes } from '../models/instance-types';
import { OrderBy } from '../utils/math';

@Injectable({
  providedIn: 'root'
})
export class PayrollManagement {
  constructor(
    private auditService: AuditService,
    private authService: AuthService,
    private companyService: CompaniesService,
    private payGridCompConfig: PayrollGridComponentConfig,
    private payrollService: PayrollService,
    private router: Router,
    @Inject('env') private env: any
  ) {}

  private _paysWithHolds: EmployeePay[] = [];
  private _unsupportedText = {
    HasMultipleLocations: 'Locations'
  };

  public sortEmployeesByFirstName: boolean = false;
  public uncalculatedPays: number[] = [];

  // #region public methods

  // Add a new payline to an already existing employee in payroll
  public addPayline(
    client: string,
    year: number,
    seq: number,
    personId: number,
    reqBody: AddPaylineRequestModel
  ): Observable<{ payroll: MasterPayrollObject; pay: EmployeePay; line: Payline }> {
    return this.payrollService.addNewPayline(client, year, seq, personId, reqBody).pipe(
      concatMap(resp => {
        return this.getPayroll(client, year, seq, false).pipe(
          map(payroll => {
            const pay = this._getEmployeePay(payroll.EmployeePays, personId);

            pay.Paylines = resp.Model;

            // assumption that the last returned payline is the one that was added
            const line = pay.Paylines[pay.Paylines.length - 1];

            this.payrollService
              .setInitialPaylineState(client, year, seq, payroll.Status, pay.PersonId, line)
              .subscribe(() => {});

            if (this._holdsOverridesApplied(payroll.Config)) {
              this.clearPresets(line, payroll, pay);
            }

            this._savePayroll(payroll);

            return { payroll, pay, line };
          })
        );
      })
    );
  }

  // Add an employee to an active payroll that did not previously include that employee
  public addPerson(
    client: string,
    year: number,
    seq: number,
    pay: EmployeePay
  ): Observable<MasterPayrollObject> {
    return this.getPayroll(client, year, seq, false).pipe(
      map(payroll => {
        // refetch the intial payroll state,
        // so this new persons' pays can be
        // 'restored to default' values
        this.payrollService.getInitialPayrollState(client, year, seq);

        // TASK 14671 - holds/overrides
        if (this._holdsOverridesApplied(payroll.Config)) {
          this.clearPresets(pay.Paylines[0], payroll, pay);
        }

        // add new employee to the payroll collection
        payroll.EmployeePays.push(pay);
        this._savePayroll(payroll);

        return payroll;
      })
    );
  }

  // Builds the 'Payroll Start Config' object
  // that drives the ability to disable the 'Start Payroll' button
  // based on unsupported client features or payroll related errors.
  public buildPayrollConfig(
    setup: ClientSetup,
    payroll: MasterPayrollObject,
    errorDetails: ErrorDetail[],
    payType: PayTypes, // specify the payroll tile or start page (manual, off cycle, etc)
    payrollStartData: PayrollStartData = null
  ): PayrollStartConfig {
    let config = new PayrollStartConfig();
    // check the client setup object for any
    // features currently unsupported in payroll
    config.Unsupported = Object.keys(setup)
      // find properties mapped in _unsupportedText
      // that is set to true in the API response
      .filter(x => setup[x] && this._unsupportedText[x])
      // map properties to human readable text
      .map(x => this._unsupportedText[x]);

    // task 18209. if client has labor allocations
    // but the feature flag is turned off, do not allow
    // payroll to start
    if (setup.HasLaborAllocation && !this.payGridCompConfig.enableLaborAllocations) {
      config.Unsupported = ['Labor Allocations'];
    }

    if (config.Unsupported.length) {
      config.IsLocked = true;
      return config;
    }
    // inspect error details collection and
    // populate errors and warnings.
    // if there are any errors, lock payroll
    if (
      (!payroll || payroll.PayType == payType || payroll.PayType == PayTypes.Manual) &&
      errorDetails &&
      errorDetails.length
    ) {
      config.Errors.push(
        ...errorDetails.filter(x => x.WarningLevel == WarningLevel.Critical).map(x => x.Message)
      );

      config.Warnings.push(
        ...errorDetails.filter(x => x.WarningLevel == WarningLevel.Warning).map(x => x.Message)
      );
      if (config.Errors.length) {
        config.IsLocked = true;
      }
    }

    // there is no active payroll, stop here
    if (!payroll) {
      return config;
    }

    const pastPayDate =
      new Date(payroll.PayDate).setUTCHours(0, 0, 0, 0) < new Date().setUTCHours(0, 0, 0, 0);

    if (payroll.PayType == PayTypes.Manual) {
      this._setManualPayLocks(payroll, payType, config);
    } else if (payroll.InputSource != PayrollInputSources.API) {
      // task 16112 force both expired and non-apollo payrolls to be locked
      config.IsLocked = true;
    } else if (pastPayDate || payroll.PayType != payType) {
      // if pay date has passed or payroll types don't match, lock start button
      config.IsLocked = true;
    } else if (payroll.Status == PayrollStatuses.Hold) {
      // task 7975 - if resuming a held payroll, clear errors collection
      // allow any CSR to resume a payroll that is on hold
      // task 12518 - payrolls with past due dates should not be started.
      // do not clear the errors collection in that scenario
      config.Errors = [];
      config.IsLocked = false;
    } else if (payroll.Status == PayrollStatuses.Started) {
      // bug 8053
      // if last edit by user is resuming, clear errors collection
      // NOTE: only the last edit by user can resume a payroll in 'started' status
      // US 36877 allow another user to resume payroll if payroll user's session has expired
      if (
        this.authService.isCurrentUser(payroll.LastEditBy) ||
        payrollStartData?.HasPayrollLastEditorSessionExpired
      ) {
        this._checkPrefundErrors(config, errorDetails, !pastPayDate);
      } else {
        config.IsLocked = true;
      }
    }

    return config;
  }

  // Calculate all of the paylines for a given EmployeePay.
  // Fired from the payroll grid when an uncalculated payline
  // has scrolled into view or after a user makes a change on a payline
  // and then moves to a new payline on the grid
  public calculatePaylines(
    client: string,
    year: number,
    seq: number,
    employeePay: EmployeePay
  ): Observable<EmployeePay> {
    const c = this._getClient(client);

    // jump through hoops to appease the API
    this._checkAndSetDefaultAllocations(c, employeePay);

    return this.payrollService
      .calculatePaylines(client, employeePay.PersonId, year, seq, employeePay.Paylines)
      .pipe(
        catchError(err => {
          // calculate payline will return 400 if any
          // have have critical errors. ensure this doesn't cancel
          // the remainder of the requests

          // If we didn't get any errors from agile API,
          // add one manually here to make sure we present the
          // issue to the user.
          let pays = err as Payline[];
          if (pays && pays.length > 0) {
            if (pays.every(p => p.Errors == null || p.Errors.length == 0)) {
              this.auditService.audit('calc-payline-missing-error', {
                ClientCode: client,
                Year: year,
                SequenceNumber: seq,
                PersonId: employeePay.PersonId
              });

              pays[0].Errors = [
                {
                  Message: 'Unknown API Error',
                  WarningLevel: WarningLevel.Critical,
                  FieldName: 'Unknown field'
                }
              ];
            }
          }

          return of(err);
        })
      )
      .pipe(
        map((response: Payline[]) => {
          if (!response || !response.length) {
            return null;
          }

          const payroll = this.GetPayrollFromCache(client, year, seq);

          this._applyTotalsToPayroll(employeePay.PersonId, response, payroll);
          employeePay.Paylines = response;

          this._savePayroll(payroll);

          this.uncalculatedPays = this.uncalculatedPays.filter(x => x != employeePay.PersonId);
          return employeePay;
        })
      );
  }

  // User clicked the Cancel Payroll button in the payroll grid component.
  // Remove the payroll item from session storage as well as any related
  // setup or session data.
  public cancelPayroll(
    client: string,
    year: number,
    sequence: number,
    activePayroll: MasterPayrollObject = null,
    bypassErrorInterceptor = false
  ): Observable<HttpResponseBase> {
    // task 7960 - delete the payroll record in the base and release client lock
    return this.payrollService
      .cancelPayroll(client, year, sequence, bypassErrorInterceptor)
      .pipe((resp: Observable<HttpResponseBase>) => {
        const payroll = activePayroll || this.GetPayrollFromCache(client, year, sequence);

        if (payroll != null) {
          this.auditService.auditPayroll(`payroll-cancelled`, {
            ...this.getAuditData(payroll)
          });
        }

        this.clearCachedPayroll(client, year, sequence);

        return resp;
      });
  }

  // Task 7976
  // Inspect a payroll and return true if it is in a state that allows
  // it to be accessed by the current user
  //   * Payrolls are accessible by anyone if they are On Hold
  //   * Payrolls in "Started" state are only accessible by the user
  //      currently working on them
  //   * Payrolls _must_ have been created by Apollo for Apollo to
  //    operate on them
  public canUserAccessPayroll(payroll: MasterPayrollObject): boolean {
    return (
      payroll.InputSource == 4 && // must be from apollo
      (payroll.Status == PayrollStatuses.Hold || // if on hold, open to anyone
        // if in process/started, only available to owner
        (payroll.Status == PayrollStatuses.Started &&
          this.authService.isCurrentUser(payroll.LastEditBy)))
    );
  }

  // in some instances the cached payroll data in session storage
  // needs to be cleared
  public clearCachedPayroll(clientCode: string, year: number, seq: number) {
    const key = this._getPayrollKey(clientCode, year, seq);
    sessionStorage.removeItem(key);

    const setupKey = this._getSetupDataKey(clientCode, year, seq);
    sessionStorage.removeItem(setupKey);

    const sessionKey = this._getSessionDataKey(clientCode, year, seq);
    sessionStorage.removeItem(sessionKey);

    const clientKey = this._getClientKey(clientCode);
    sessionStorage.removeItem(clientKey);
  }

  // handle logic for holds and overrides set in the payroll config object
  public clearPresets(payline: Payline, payroll: MasterPayrollObject, pay: EmployeePay) {
    const config = payroll.Config;
    if (config.HoldEarnings.length) {
      this._clearEarnings(payline, config, pay);
    }

    if (config.HoldDeductions.length) {
      this._clearDeductions(payline, config, pay);
    }

    if (config.HoldTaxes.length) {
      this._clearTaxes(payline, config, pay);
    }

    if (config.HoldDirectDeposit) {
      payline.DirectDeposits.map(x => (x.TemporaryAmount = 0));
      this._setUncalculatedPay(pay, true);
    }

    if (config.PayrollFrequencyOverride) {
      payline.PayFrequencyTaxOverride = config.PayrollFrequencyOverride;
    }
  }

  // Create a Worksheet to be used on the auto payroll grid component
  // see task 7972 for more information
  public createAutoPayWorksheet(payDate: PayDateInfo): Observable<MasterPayrollObject> {
    return this.payrollService
      .getPayrollConfigSetupData(
        payDate.ClientId,
        [new ScheduleRequest(payDate.CalendarId, 0, payDate.PeriodEnd)],
        payDate.LocationId,
        payDate.TargetPayDate,
        PayTypes.Regular // all autopay worksheets are "Regular" pay types.
      )
      .pipe(
        concatMap(payrollConfig => {
          let config = new PayrollConfig();

          return this.payrollService
            .getEmployeePays(
              payDate,
              config,
              Array.from(String(payDate.CalendarId), num => Number(num))
            )
            .pipe(
              concatMap(pays => {
                // Task 7532 add pay frequency (of the calendar) to each payline
                if (pays && pays.length && payDate.Frequency) {
                  pays.forEach(pay => {
                    pay.Paylines.forEach(line => {
                      line.PayFrequency = payDate.Frequency;
                    });
                  });
                }

                // build master payroll object
                const payroll = new MasterPayrollObject(
                  payDate.ClientId,
                  [
                    new Schedule(
                      payDate.CalendarId,
                      0,
                      payDate.PeriodEnd,
                      payDate.Frequency,
                      payDate.CalendarName,
                      payDate.PeriodBegin,
                      payDate.PeriodEnd,
                      payDate.TargetPayDate
                    )
                  ],
                  payDate.LocationId,
                  payDate.TargetPayDate,
                  false,
                  false,
                  PayrollStatuses.Started,
                  this.authService.getCurrentUserName(),
                  new Date()
                );

                payroll.Config = config;
                payroll.EmployeePays = pays;

                // update apollo's DB and return the paygrid's number
                return this.payrollService.createPayGrid(payDate.ClientId, payroll).pipe(
                  map(payGridId => {
                    payroll.Id = payGridId;
                    return payroll;
                  })
                );
              })
            );
        })
      );
  }

  // Create a new (non automatic) payroll record in the base
  // fetch associated setup and session data necessary to
  // initialize the payroll and store all of that data in
  // session storage.
  public createPayroll(args: CreatePayrollArgs) {
    return (
      this.payrollService
        .createPayroll(args)
        // stitch employee pays into master payroll object
        .pipe(
          concatMap(payroll => {
            // NOTE: APS does not return the payroll config in the create payroll response
            payroll.Config = args.Config;
            this._trimTaxCodes(payroll);
            return of(payroll);
          }),
          concatMap(payroll => {
            return this.payrollService
              .getPayrollConfigSetupData(
                payroll.ClientCode,
                payroll.Calendars,
                payroll.LocationId,
                payroll.PayDate,
                payroll.PayType
              )
              .pipe(
                map(configSetupData => {
                  const key = this._getSetupDataKey(
                    args.ClientCode,
                    new Date(args.PayDate).getFullYear(),
                    payroll.SequenceNumber
                  );
                  sessionStorage.setItem(key, JSON.stringify(configSetupData));
                  return payroll;
                })
              );
          }),
          concatMap(payroll => {
            // fetch config data necessary to assemble a payroll
            // task 8207 - update pay grid session data
            const sessionData = new PayGridSessionData(payroll);
            return this.payrollService.createPayGridSessionData(sessionData).pipe(
              map(() => {
                const key = this._getSessionDataKey(
                  args.ClientCode,
                  new Date(args.PayDate).getFullYear(),
                  payroll.SequenceNumber
                );
                sessionStorage.setItem(key, JSON.stringify(sessionData));
                return payroll;
              })
            );
          }),
          concatMap((payroll: MasterPayrollObject) => {
            return this.companyService.getCompany(payroll.ClientCode).pipe(
              map(client => {
                const key = this._getClientKey(client.Id);
                sessionStorage.setItem(key, JSON.stringify(client));
                return payroll;
              })
            );
          }),
          map(payroll => {
            this._initPayroll(payroll);
            return payroll;
          })
        )
    );
  }

  // task 19257. if a rated earning has been allocated, do not display a value
  // in the payroll grid and detail editor, as it would not be accurate
  public earningHoursAllocated(line: Payline, config: CompanyEarningConfig) {
    // column is not an earning, or no allocations are set on this payline
    if (!line || !line.Earnings.find(x => x.EarningId == config.Id) || !line.Allocations) {
      return false;
    }

    const vals = Object.values(line.Allocations);

    let allocated = false;

    for (let val of vals) {
      const v = val.TemporaryHourlyAllocations;
      if (!v || !v[config.Id] || !v[config.Id].Hours || +v[config.Id].Hours == 0) {
        continue;
      }

      allocated = true;
      break;
    }

    return allocated;
  }

  // task 19257. if a rated earning has been allocated, do not display a value
  // in the payroll grid and detail editor, as it would not be accurate
  public earningRateAllocated(line: Payline, config: CompanyEarningConfig) {
    // column is not an earning, or no allocations are set on this payline
    if (!line || !line.Earnings.find(x => x.EarningId == config.Id) || !line.Allocations) {
      return false;
    }

    const vals = Object.values(line.Allocations);

    let allocated = false;

    for (let val of vals) {
      const v = val.TemporaryHourlyAllocations;
      if (
        !v ||
        !v[config.Id] ||
        !v[config.Id].TemporaryRateAmount ||
        +v[config.Id].TemporaryRateAmount == 0
      ) {
        continue;
      }

      allocated = true;
      break;
    }

    return allocated;
  }

  // task 8000
  // does not consult the base for payroll records
  // since automatic worksheets do not exist there
  public findMatchingWorksheet(
    clientId: string,
    locationId: number,
    calendarId: number,
    payDate: Date
  ): Observable<MasterPayrollObject | null> {
    // task 8136 - use the FindWorksheet call for auto payrolls
    // as for these, we'll query our internal cache/paygrids
    // and for non-autos, we'll reach out to the base for such data.
    return this.payrollService.findWorksheet(clientId, locationId, calendarId, payDate).pipe(
      concatMap(payGridId => {
        if (!payGridId) {
          return of(null);
        }

        return this.payrollService.getPayGrid(clientId, payGridId).pipe(
          map(payGrid => {
            payGrid.Id = payGridId;
            return payGrid;
          })
        );
      })
    );
  }

  // Get the payroll related audit data that should be tied to
  // each payroll audit event.
  public getAuditData(payroll: MasterPayrollObject): IPayrollEventData {
    return {
      Calendars: payroll.Calendars,
      ClientCode: payroll.ClientCode,
      SequenceNumber: payroll.SequenceNumber,
      PayDate: payroll.PayDate,
      LocationId: payroll.LocationId,
      PayrollType: payroll.PayType
    };
  }

  // Returns the employee's full name, including the middle name if one exists
  public getEmployeeFullName(e: EmployeePay) {
    const first = e.EmployeeFirstName?.trim() ? `${e.EmployeeFirstName.trim()} ` : '';
    const middle = e.EmployeeMiddleName?.trim() ? `${e.EmployeeMiddleName.trim()} ` : ' ';
    const last = e.EmployeeLastName?.trim() ? `${e.EmployeeLastName.trim()} ` : '';

    return `${first}${middle}${last}`;
  }

  // return {last name} {first name} {middle name}
  // used in paygrid default sorting
  public getEmployeeLastFirstName(e: EmployeePay) {
    const last = e.EmployeeLastName ? `${e.EmployeeLastName.trim()} ` : '';
    const first = e.EmployeeFirstName ? `${e.EmployeeFirstName.trim()} ` : '';
    const middle = e.EmployeeMiddleName ? `${e.EmployeeMiddleName.trim()}` : '';

    return `${last}${first}${middle}`;
  }

  // pull employee pay from session storage if payroll exists in session storage
  // if not fetch from API
  public getEmployeePay(
    client: string,
    year: number,
    seq: number,
    personId: number
  ): Observable<EmployeePay> {
    return this.getEmployeePays(client, year, seq).pipe(
      map(resp => {
        return this._getEmployeePay(resp, personId);
      })
    );
  }

  // pull payline from session storage if it exists, if not fetch from API
  public getEmployeePayline(
    client: string,
    year: number,
    seq: number,
    personId: number,
    paylineId: number
  ) {
    return this.getEmployeePay(client, year, seq, personId).pipe(
      map(resp => {
        return this._getEmployeePayline(resp, paylineId);
      })
    );
  }

  // pull employee pays from session storage if payroll exists in session storage
  // if not fetch from API
  public getEmployeePays(client: string, year: number, seq: number): Observable<EmployeePay[]> {
    return this.getPayroll(client, year, seq).pipe(
      map(resp => {
        return resp.EmployeePays;
      })
    );
  }
  public GetPayrollFromCache(client: string, year: number, seq: number): MasterPayrollObject {
    const key = this._getPayrollKey(client, year, seq);
    const payroll = sessionStorage.getItem(key);

    if (!payroll) {
      return null;
    }

    const p: MasterPayrollObject = JSON.parse(payroll);
    return p;
  }

  // Fetch the latest instance of the MasterPayrollObject that includes EmployeePays.
  // If an instance exists in session storage (and all other necessary exists),
  // return the payroll
  // If not, retrieve and assemble the full payroll and write to session storage.
  // If forceRefresh if true, pull from the API no matter if it exists in session storage.
  public getPayroll(
    client: string,
    year: number,
    seq: number,
    forceRefresh: boolean = false
  ): Observable<MasterPayrollObject> {
    if (!this._isPayrollDataPopulated(client, year, seq) || forceRefresh) {
      return this.payrollService.getPayroll(client, year, seq).pipe(
        concatMap(payroll => {
          return this._stitchEmployeePays(payroll);
        }),
        concatMap(payroll => {
          return this.payrollService
            .getPayrollConfigSetupData(
              payroll.ClientCode,
              payroll.Calendars,
              payroll.LocationId,
              payroll.PayDate,
              payroll.PayType
            )
            .pipe(
              map(configSetupData => {
                const key = this._getSetupDataKey(client, year, seq);
                sessionStorage.setItem(key, JSON.stringify(configSetupData));
                return payroll;
              })
            );
        }),
        concatMap(payroll => {
          return this.payrollService
            .getPayGridSessionData(
              payroll.ClientCode,
              payroll.LocationId,
              payroll.Year,
              payroll.SequenceNumber
            )
            .pipe(
              map(payGridSessionData => {
                const key = this._getSessionDataKey(client, year, seq);
                sessionStorage.setItem(key, JSON.stringify(payGridSessionData));
                return payroll;
              })
            );
        }),
        concatMap((payroll: MasterPayrollObject) => {
          return this.companyService.getCompany(payroll.ClientCode).pipe(
            map(client => {
              const key = this._getClientKey(payroll.ClientCode);
              sessionStorage.setItem(key, JSON.stringify(client));
              return payroll;
            })
          );
        }),
        map(resp => {
          this._initPayroll(resp);
          this._savePayroll(resp);
          return resp;
        })
      );
    } else {
      return of(this.GetPayrollFromCache(client, year, seq));
    }
  }

  // Manual Payrolls do not need to fetch setup/session data
  public getManualPayroll(
    client: string,
    year: number,
    seq: number
  ): Observable<MasterPayrollObject> {
    const p = this.GetPayrollFromCache(client, year, seq);
    if (!p) {
      return this.payrollService.getPayroll(client, year, seq).pipe(
        concatMap(payroll => {
          return this._stitchEmployeePays(payroll).pipe(
            map(payroll => {
              this._savePayroll(payroll);
              return payroll;
            })
          );
        })
      );
    } else {
      return of(p);
    }
  }

  // Adjustment Payrolls do not need to fetch setup/session data
  public getAdjustmentPayroll(
    client: string,
    year: number,
    seq: number
  ): Observable<MasterPayrollObject> {
    const p = this.GetPayrollFromCache(client, year, seq);
    if (!p) {
      return this.payrollService.getPayroll(client, year, seq).pipe(
        concatMap(payroll => {
          return this._stitchEmployeePays(payroll).pipe(
            map(payroll => {
              this._savePayroll(payroll);
              return payroll;
            })
          );
        })
      );
    } else {
      return of(p);
    }
  }

  // Fetch the paygrid session data. A payroll must first be created or fetched
  // via the payroll management service for the session data to be populated
  public getSessionData(client: string, year: number, seq: number) {
    if (!this._isPayrollDataPopulated(client, year, seq)) {
      throw new Error(
        'A payroll must first be created or fetched in order to access the session data'
      );
    }

    const sessionData = this._getSessionData(client, year, seq);

    return sessionData;
  }

  // Fetch the payroll setup data. A payroll must first be created or fetched
  // via the payroll management service for the setup data to be populated
  public getSetupData(client: string, year: number, seq: number) {
    if (!this._isPayrollDataPopulated(client, year, seq)) {
      throw new Error(
        'A payroll must first be created or fetched in order to access the setup data'
      );
    }

    return this._getSetupData(client, year, seq);
  }

  public hasLaborAllocations(employeePay: EmployeePay, payline: Payline) {
    if (payline?.Allocations == null || Object.keys(payline.Allocations).length == 0) {
      return false;
    }

    // NOTE: for labor allocation clients and paylines that have no allocation set,
    // the front end is synthetically setting a single 100% allocation record to
    // either the person's home department or 'None' depending on client allocation type.
    // if there is a single allocation record set as 100% to home dept or None, return false here
    // so the button on the payline shows 'Add' rather than 'Update'. these are the types of things
    // that turn code into spaghetti
    const key = Object.keys(payline.Allocations)[0];
    if (
      Object.keys(payline.Allocations).length == 1 &&
      (+key == 0 || key == employeePay.DepartmentId.toString())
    ) {
      // if permanent percentage is 100, or some hourly/wage allocations have been set
      const alloc = payline.Allocations[key];
      if (
        !alloc.TemporaryHourlyAllocations ||
        !Object.values(alloc.TemporaryHourlyAllocations).find(
          x => x.Hours || x.TemporaryRateAmount || x.PermanentRateAmount
        )
      ) {
        return false;
      }
    }

    return Object.values(payline.Allocations).find((a: LaborAllocation) => {
      return (
        a.PermanentPercentageAllocations != null ||
        a.TemporaryPercentageAllocations != null ||
        Object.values(a.TemporaryHourlyAllocations).find(
          x => x.Hours || x.PermanentRateAmount || x.TemporaryRateAmount
        )
      );
    });
  }

  // tasks 18212/3
  // if payline has any allocations set by wage/hourly, disable all earning controls
  // on the paygrid and detail editor
  public isEarningEditable(line: Payline, earningId: string) {
    // column is not an earning, or no allocations are set on this payline
    if (!line.Earnings.find(x => x.EarningId == earningId) || !line.Allocations) {
      return true;
    }

    const vals = Object.values(line.Allocations);

    if (
      vals &&
      vals.find(
        x => x.TemporaryHourlyAllocations && Object.keys(x.TemporaryHourlyAllocations).length
      )
    ) {
      return false;
    }

    return true;
  }

  public isPaylineEditable(paylineType: PaylineType) {
    let editableTypes: PaylineType[];
    if (
      this.env.instanceType == InstanceTypes.CsrPortal ||
      this.authService.hasPermission('CanEnterNoChecks', 'W')
    ) {
      // FIXME: CanEnterNoChecks does not exist, this is not validated for a future client portal addition
      editableTypes = [PaylineType.Normal, PaylineType.NoCheck];
    } else {
      editableTypes = [PaylineType.Normal];
    }

    return editableTypes.includes(paylineType);
  }

  // Validation setup to check if the client should be rerouted to the automatic payroll component
  public isClientSetupForAutoPayroll(locations: CompanyLocationConfig[]): boolean {
    // client has a single location. that location has a single calendar with IsPayrollAuto = true
    return (
      locations &&
      locations.length == 1 &&
      locations[0].Calendars.length == 1 &&
      locations[0].Calendars[0].IsPayrollAuto
    );
  }

  public isAutoPayrollSkipped(clientCode: string, locationId: number, calendarId: number) {
    return this.payrollService.getNextPayDate(clientCode, locationId, calendarId).pipe(
      concatMap(pay => {
        return this.findMatchingWorksheet(
          clientCode,
          locationId,
          calendarId,
          pay.TargetPayDate
        ).pipe(
          map(worksheet => {
            return worksheet && worksheet.IsAutomaticSkipped;
          })
        );
      })
    );
  }

  public onPayrollDeactivate(
    payroll: MasterPayrollObject,
    paygridDeactivate: boolean,
    rowData?: any,
    nextState?: RouterStateSnapshot,
    paygridSessionData?: PayGridSessionData
  ): Observable<boolean> | boolean {
    // if a user that is NOT the current active payroll user is navigating away from
    // payroll-grid or payroll-preview - do nothing. Apollo could be bouncing users that
    // tried to access a payroll, but are no longer the active payroll user
    // NOTE: see task 7976 for more info on comparing user.LegacyUserId to payroll.LastEditBy
    if (!payroll || !this.authService.isCurrentUser(payroll.LastEditBy)) {
      return true;
    }

    // bug 8047, do not fire status update changes if the payroll has been cancelled
    if (
      payroll.IsCancelled ||
      payroll.Status == PayrollStatuses.Approved ||
      payroll.Status == PayrollStatuses.Saved
    ) {
      return true;
    }

    // Task 7977
    // fire request to change payroll status to 'On Hold'
    // if the user is navigating anywhere except
    // the payroll grid or payroll preview page
    // task 9631 - if user is navigating directly to the add person page,
    // short circuit the logic that sets the payroll status to On Hold
    let updateStatus = true;

    if (
      nextState &&
      (payrollGridRouteRegex.test(nextState.url) ||
        payrollPreviewRouteRegex.test(nextState.url) ||
        addNewPersonRouteRegex.test(nextState.url))
    ) {
      updateStatus = false;
    }

    return this.authService.isLoggedIn().pipe(
      concatMap(loggedIn => {
        if (paygridDeactivate && loggedIn) {
          return this._calculateUncalcdPaylines(
            rowData,
            payroll.ClientCode,
            payroll.Year,
            payroll.SequenceNumber
          ).pipe(
            map(() => {
              return loggedIn;
            })
          );
        } else {
          return of(loggedIn);
        }
      }),
      concatMap(loggedIn => {
        if (!loggedIn || !updateStatus) {
          return of(true);
        }
        // user is leaving the payroll grid and no changes have ever been made on this payroll.
        // cancel the payroll
        else if (paygridDeactivate && !paygridSessionData?.PayrollModified) {
          return this.cancelPayroll(
            payroll.ClientCode,
            payroll.Year,
            payroll.SequenceNumber,
            payroll
          ).pipe(
            map(() => {
              return true;
            })
          );
        }
        // user is either leaving the preview report or
        // leaving the payroll grid that had some changes (this session or past).
        // set the status to on hold
        else if (!paygridDeactivate || paygridSessionData?.PayrollModified) {
          payroll.Status = PayrollStatuses.Hold;
          return this.payrollService
            .updatePayrollStatus(
              payroll.ClientCode,
              payroll.Year,
              payroll.SequenceNumber,
              payroll.Status
            )
            .pipe(
              map(() => {
                this.auditService.auditPayroll(
                  `payroll-status-set-to-hold`,
                  this.getAuditData(payroll)
                );
                return true;
              })
            );
        } else {
          return of(true);
        }
      }),
      catchError(err => {
        console.log(err);
        return of(true);
      })
    );
  }

  public onCorrectionPayrollDeactivate(payroll: MasterPayrollObject) {
    if (
      !payroll ||
      !this.authService.isCurrentUser(payroll.LastEditBy) ||
      payroll.Status != PayrollStatuses.Started
    ) {
      return true;
    }

    return this.authService.isLoggedIn().pipe(
      concatMap(loggedIn => {
        if (!loggedIn) {
          return of(true);
        }

        return this.payrollService
          .updatePayrollStatus(
            payroll.ClientCode,
            payroll.Year,
            payroll.SequenceNumber,
            PayrollStatuses.Hold
          )
          .pipe(
            map(() => {
              this.auditService.auditPayroll(
                `payroll-status-set-to-hold`,
                this.getAuditData(payroll)
              );
              return true;
            })
          );
      }),
      catchError(err => {
        console.log(err);
        return of(true);
      })
    );
  }

  public onManualPayrollDeactivate(payroll: MasterPayrollObject): Observable<boolean> | boolean {
    if (!payroll || !this.authService.isCurrentUser(payroll.LastEditBy)) {
      return true;
    }

    return this.authService.isLoggedIn().pipe(
      concatMap(loggedIn => {
        if (!loggedIn) {
          return of(true);
        }

        return this.payrollService
          .saveManualPayroll(payroll.ClientCode, payroll.Year, payroll.SequenceNumber)
          .pipe(
            map(() => {
              this.auditService.auditPayroll(`manual-payroll-saved`, this.getAuditData(payroll));
              return true;
            })
          );
      }),
      catchError(err => {
        console.log(err);
        return of(true);
      })
    );
  }

  // return 'Multiple' if the payroll has employee pays with
  // differing frequencies. if they are all the same, return the name
  // of that frequency.
  // optional boolean for the payroll preview page to only look at
  // pays that have OkToPay set to true
  public payrollFrequencyDisplay(pays: EmployeePay[], okToPayOnly: boolean = false) {
    let frequencies = [];

    for (let pay of pays) {
      for (let line of pay.Paylines) {
        if (okToPayOnly && !line.OkToPay) {
          continue;
        }

        frequencies.push(line.PayFrequencyTaxOverride || pay.PayFrequency);
      }
    }

    frequencies = frequencies.filter((value, index, array) => array.indexOf(value) === index);

    return frequencies.length > 1 ? 'Multiple' : PayrollFrequency[frequencies[0]];
  }

  datePipe = new DatePipe('en-Us');

  public periodBeginDisplay(payroll: MasterPayrollObject) {
    const periodBegins = payroll.Calendars.map(x => x.PeriodBegin);
    return periodBegins.filter((x, i) => periodBegins.indexOf(x) === i).length > 1
      ? 'Multiple'
      : this.datePipe.transform(periodBegins[0], 'MM/dd/yyyy');
  }

  // if each employee has the same period end value, display that value
  // otherwise display the string 'Multiple'
  // the employee pay record as well as each payline needs to be checked
  // as the period end can be overriden at the payline level
  public periodEndDisplay(payroll: MasterPayrollObject, okToPayOnly: boolean = false) {
    const periodEnds = [];

    for (let pay of payroll.EmployeePays) {
      for (let line of pay.Paylines) {
        if (okToPayOnly && !line.OkToPay) {
          continue;
        }

        periodEnds.push(
          new Date(line.PeriodEndOverride || pay.PeriodEnd).toISOString().slice(0, 10)
        );
      }
    }

    return periodEnds.filter((x, i) => periodEnds.indexOf(x) === i).length > 1
      ? 'Multiple'
      : this.datePipe.transform(periodEnds[0], 'MM/dd/yyyy');
  }

  public sortEmployeesByLastName(employeePays: EmployeePay[]) {
    const pays = employeePays.sort((a, b) => {
      if (a.EmployeeFirstName == b.EmployeeFirstName && a.EmployeeLastName == b.EmployeeLastName) {
        return a.EmployeeMiddleName > b.EmployeeMiddleName ? 1 : -1;
      } else {
        return `${a.EmployeeLastName} ${a.EmployeeFirstName}` >
          `${b.EmployeeLastName} ${b.EmployeeFirstName}`
          ? 1
          : -1;
      }
    });

    return pays;
  }

  // task 6918 - presort grid according to base rules
  public sortEmployeePays(
    employeePays: EmployeePay[],
    configSetupData: PayrollConfigSetupData,
    sortByLastName: boolean = null
  ) {
    let sort = configSetupData.SortFields;
    this.sortEmployeesByFirstName = sortByLastName === false;

    if (sort && sort.length) {
      // asc sort on each field passed, in the order they appear
      // ------
      // 1 - EmployeeName: sort by employee Last, First name
      // 2 - PersonId: sort by EmployeePay.PersonId
      // 3 - DivisionId: sort by EmployeePay.DivisionId
      // 4 - DepartmentId: sort by EmployeePay.DepartmentId
      employeePays.sort((a, b) => {
        if (sort.length > 1 && this._arePaylinePropsEqual(sort[0], a, b)) {
          if (sort.length > 2 && this._arePaylinePropsEqual(sort[1], a, b)) {
            if (sort.length > 3 && this._arePaylinePropsEqual(sort[2], a, b)) {
              // there are more than 3 sort fields and the previous 3 sort values are equal
              return this._preSortPaylineByKey(sort[3], a, b);
            }

            // there are more than 2 sort fields and the previous 2 sort values are equal
            return this._preSortPaylineByKey(sort[2], a, b);
          }

          // there is more than 1 sort field and the previous sort values are equal
          return this._preSortPaylineByKey(sort[1], a, b);
        }

        // there is only 1 sort field, sort on that
        return this._preSortPaylineByKey(sort[0], a, b);
      });
    }
  }

  public sortPaylinesBySeq(payroll: MasterPayrollObject, pay?: EmployeePay) {
    if (pay != null) {
      pay.Paylines = OrderBy(pay.Paylines, p => p.PaylineSeq);
    } else {
      for (let empPay of payroll.EmployeePays) {
        empPay.Paylines = OrderBy(empPay.Paylines, p => p.PaylineSeq);
      }
    }
  }

  // Task #26431 - re-sort paylines by type and paylineId
  // manual check -> history void (sorted by paydate) -> regular (sorted by paylineId)
  public sortPaylinesByType(payroll: MasterPayrollObject, pay?: EmployeePay) {
    const sortGrade = {
      [PaylineType.Manual]: 0,
      [PaylineType.NoCheck]: 1,
      [PaylineType.Void]: 2,
      [PaylineType.Normal]: 3
    };

    const _sort = (a: Payline, b: Payline) => {
      if (a.PaylineType != b.PaylineType) {
        return sortGrade[a.PaylineType] > sortGrade[b.PaylineType] ? 1 : -1;
      } else {
        return a.PaylineId > b.PaylineId ? 1 : -1;
      }
    };

    if (pay != null) {
      pay.Paylines = pay.Paylines.sort(_sort);
    } else {
      for (let empPay of payroll.EmployeePays) {
        empPay.Paylines = empPay.Paylines.sort(_sort);
      }
    }
  }

  public startResumeManualPayroll(clientCode: string, payrollStartData: PayrollStartData) {
    const p = payrollStartData?.ActivePayroll;
    if (p && p.PayType == PayTypes.Manual) {
      // TASK 20226
      if (p.Status == PayrollStatuses.Started) {
        // fetch the payroll and employee pays.
        this.getManualPayroll(clientCode, p.Year, p.SequenceNumber).subscribe(payroll => {
          this._navigateToManualPayroll(payroll.ClientCode, payroll.Year, payroll.SequenceNumber);
        });
      } else if (p.Status == PayrollStatuses.Saved) {
        // fire the Start/Resume manual payroll request.
        this.startManualPay(clientCode, p.Year, payrollStartData);
      }
    } else if (!p) {
      // bug 38485. don't use current calendar year.
      // if the next available paydate is in the next calendar year, use that.
      const pd = payrollStartData.Locations[0]?.NextPayDates[0]?.TargetPayDate;
      const date = pd ? new Date(pd) : new Date();

      this.startManualPay(clientCode, date.getFullYear(), payrollStartData);
    }
  }

  //Promoted this to Public so I can re-start a Manual Pay after it's been saved
  //Needed for the functionality to print manual check paystubs.
  public startManualPay(clientCode: string, year: number, payrollStartData: PayrollStartData) {
    this.payrollService
      .startManualPayroll(clientCode, year, payrollStartData.Locations[0].Id)
      .subscribe(payroll => {
        this._savePayroll(payroll);
        this._navigateToManualPayroll(clientCode, payroll.Year, payroll.SequenceNumber);
      });
  }

  // update the employee pay object in session storage
  public updateEmployeePay(client: string, year: number, seq: number, employeePay: EmployeePay) {
    const payroll = this.GetPayrollFromCache(client, year, seq);

    const index = payroll.EmployeePays.findIndex(x => x.PersonId == employeePay.PersonId);

    if (index == -1) {
      payroll.EmployeePays.push(employeePay);
    } else {
      payroll.EmployeePays[index] = employeePay;
    }

    this._savePayroll(payroll);
  }

  // Update payline in session storage and set the payline to be calculated
  // by the payroll grid. Intended to be fired when a user makes a change to a cell
  // in the grid.  Returns the updated payroll object.
  public updatePayline(
    client: string,
    year: number,
    seq: number,
    personId: number,
    payline: Payline,
    setToCalc: boolean = false
  ) {
    const payroll = this.GetPayrollFromCache(client, year, seq);

    const pay = this._getEmployeePay(payroll.EmployeePays, personId);

    const line = this._getEmployeePayline(pay, payline.PaylineId);

    const payIdx = payroll.EmployeePays.indexOf(pay);
    const lineIdx = pay.Paylines.indexOf(line);

    if (lineIdx == -1) {
      payroll.EmployeePays[payIdx].Paylines.push(payline);
    } else {
      payroll.EmployeePays[payIdx].Paylines[lineIdx] = payline;
    }

    this._savePayroll(payroll);

    if (setToCalc) {
      // flag this personId as needing to be calculated
      this._setUncalculatedPay(pay);
    }

    return payroll;
  }

  // Update the session data object after the user edits a cell or a payline is calculated
  public updatePayGridSessionData(
    client: string,
    year: number,
    seq: number,
    sessionData: PayGridSessionData,
    setPayrollModified = true
  ) {
    if (setPayrollModified) {
      sessionData.PayrollModified = true;
    }
    this.getPayroll(client, year, seq).subscribe(payroll => {
      return this.payrollService
        .updatePayGridSessionData(sessionData, payroll.LocationId)
        .subscribe(() => {
          const key = this._getSessionDataKey(client, year, seq);
          sessionStorage.setItem(key, JSON.stringify(sessionData));
        });
    });
  }

  public updateCachedStatus(client: string, year: number, seq: number, status: PayrollStatuses) {
    if (!this._isPayrollDataPopulated(client, year, seq)) {
      return;
    }

    const payroll = this.GetPayrollFromCache(client, year, seq);
    payroll.Status = status;

    const key = this._getPayrollKey(client, year, seq);
    sessionStorage.setItem(key, JSON.stringify(payroll));
  }

  public getAllowedPaylineTypes() {
    var types = [PaylineType.Normal];
    var isCSR = this.authService.isInAnyRole(['CSR', 'CSR1']);

    // History Void
    if (isCSR) {
      types.push(PaylineType.Void);
    }

    // No Check
    if (isCSR || this.authService.isInAnyRole(['CreateNoCheckPaylines'])) {
      types.push(PaylineType.NoCheck);
    }

    return types;
  }

  // #endregion

  // #region private methods

  // US 14489
  // when all earnings are held, default OkToPay to false for all employees
  // on initial page load
  private _allEarningsHeld(payline: Payline, config: PayrollConfig) {
    const a = config.HoldEarnings.sort((a, b) => a.localeCompare(b));
    const b = payline.Earnings.map(x => x.EarningId).sort((a, b) => a.localeCompare(b));

    // return true if all of the earnings in the payline are held
    return b.every(x => a.includes(x));
  }

  // given a response from the calculate-paylines endpoint, add any errors or calculated totals to our payroll model
  private _applyTotalsToPayroll(
    personId: number,
    totalsResponse: Payline[],
    payroll: MasterPayrollObject
  ) {
    // grab the employeePay that we need to update
    let pay = payroll.EmployeePays.find(p => p.PersonId == personId);

    // loop over our returned paylines; match them to the paylines in our model
    // and set their totals
    for (let calc of totalsResponse) {
      // get the matching payline in our model
      let match = pay.Paylines.find(p => p.PaylineId == calc.PaylineId);
      if (!match) continue;

      match.QTDEarningsGross = calc.QTDEarningsGross;
      match.NetAmountPaid = calc.NetAmountPaid;
      match.NetCheckAmountPaid = calc.NetCheckAmountPaid;
      match.NetAmountDeposited = calc.NetAmountDeposited;
      match.EarningsTotalCurrentAmount = calc.EarningsTotalCurrentAmount;
      match.EarningsTotalCurrentHours = calc.EarningsTotalCurrentHours;
      match.EarningsTotalYTDAmount = calc.EarningsTotalYTDAmount;
      match.EarningsTotalYTDHours = calc.EarningsTotalYTDHours;
      match.TaxesTotalCurrentAmount = calc.TaxesTotalCurrentAmount;
      match.DeductionsTotalAmount = calc.DeductionsTotalAmount;
      match.Errors = calc.Errors;

      // loop over earnings and apply totals
      for (let calcEarn of calc.Earnings) {
        let matchEarn = match.Earnings.find(e => e.EarningId == calcEarn.EarningId);
        if (matchEarn) {
          matchEarn.CurrentAmount = calcEarn.CurrentAmount;
          matchEarn.YTDAmount = calcEarn.YTDAmount;
          matchEarn.YTDHours = calcEarn.YTDHours;

          // bug 7834 ensure "use rate from" earning rates get updated
          matchEarn.TemporaryRateAmount = calcEarn.TemporaryRateAmount;
          matchEarn.PermanentRateAmount = calcEarn.PermanentRateAmount;
        }
      }

      // loop over deductions and apply totals
      for (let calcDed of calc.Deductions) {
        let matchDed = match.Deductions.find(d => d.DeductionCode == calcDed.DeductionCode);
        if (matchDed) {
          // matchDed.DeductionPaidBy = calcDed.DeductionPaidBy;
          matchDed.CurrentAmount = calcDed.CurrentAmount;
          matchDed.YTDAmount = calcDed.YTDAmount;
        }
      }

      // loop over taxes and apply totals
      for (let calcTax of calc.Taxes) {
        let matchTax = match.Taxes.find(t => t.TaxCode == calcTax.TaxCode);
        if (matchTax) {
          matchTax.CurrentAmount = calcTax.CurrentAmount;
          matchTax.YTDAmount = calcTax.YTDAmount;
        }
      }

      // loop of deposits and apply totals
      for (let calcDeposit of calc.DirectDeposits) {
        let matchDeposit = match.DirectDeposits.find(
          d => d.DirectDepositId == calcDeposit.DirectDepositId
        );
        if (matchDeposit) {
          matchDeposit.CurrentAmount = calcDeposit.CurrentAmount;
        }
      }
    }
  }

  private _arePaylinePropsEqual(key: number, a: EmployeePay, b: EmployeePay) {
    // check if payline properties are equal by sort key property
    // in order to further sort by another property. see _preSortPaylineByKey for definitions
    if (key === 1) {
      return this.sortEmployeesByFirstName
        ? this.getEmployeeFullName(a).toLowerCase() == this.getEmployeeFullName(b).toLowerCase()
        : this.getEmployeeLastFirstName(a).toLowerCase() ==
            this.getEmployeeLastFirstName(b).toLowerCase();
    } else if (key === 2) {
      return a.EmployeeNumber == b.EmployeeNumber;
    } else if (key === 3) {
      return a.DivisionId == b.DivisionId;
    } else if (key === 4) {
      return a.DepartmentId == b.DepartmentId;
    }
    return 0;
  }

  // Calculate any paylines on the grid with changes that have not yet been calculated for use in route deactivate
  private _calculateUncalcdPaylines(
    rowData: any,
    client: string,
    year: number,
    seq: number
  ): Observable<boolean> {
    const personIds = [];
    let paysToCalc = [];

    paysToCalc = rowData
      .filter(r => this.uncalculatedPays.includes(r.PersonId))
      .map(r => {
        if (personIds.includes(r.PersonId)) {
          return of(null);
        }

        personIds.push(r.PersonId);
        return this.getEmployeePay(client, year, seq, r.PersonId).pipe(
          concatMap((pay: EmployeePay) => {
            return this.calculatePaylines(client, year, seq, pay);
          })
        );
      });

    if (!paysToCalc.length) {
      return of(true);
    } else {
      return forkJoin(paysToCalc).pipe(
        map(() => {
          this._setPaylinesCalculated(personIds, client, year, seq);
          return true;
        }),
        catchError(_ => of(true))
      );
    }
  }

  // when running a payroll for a labor allocation client
  // but the payroll does not have any labor allocations set,
  // we must pass the API a 100% allocation to the person's
  // home department if the client allocates by department.
  // if client allocates by job code, just pass 'None'.
  // this is not tied to a bug number, but has come out of
  // integration testing conversations on 3/9/2023. --jb
  private _checkAndSetDefaultAllocations(client: Client, pay: EmployeePay) {
    const types = LaborAllocationTypes;
    if (client.LaborAllocationType == types.None) {
      return;
    }

    for (let line of pay.Paylines) {
      if (line.Allocations && Object.keys(line.Allocations).length > 0) {
        continue;
      }

      const key = client.LaborAllocationType == types.Department ? pay.DepartmentId : '0';

      line.Allocations = {};
      line.Allocations[key] = new LaborAllocation(100);
    }
  }

  private _checkPrefundErrors(
    config: PayrollStartConfig,
    errorDetails: ErrorDetail[],
    futurePayDate: boolean
  ) {
    // we need to check if a prefundDays error is within and reject if so
    // also check to make sure the pay date is in the future
    const hasPrefundError = errorDetails && !errorDetails.find(ed => ed.FieldName == 'PrefundDays');
    if (hasPrefundError && futurePayDate) {
      config.Errors = [];
    } else {
      // prefundDays warning in-effect but let's clear out the irrelevant on-hold error
      if (config.Errors.find(a => a.includes('There is a payroll currently being processed by'))) {
        let index = config.Errors.findIndex(i =>
          i.includes('There is a payroll currently being processed by')
        );

        config.Errors.splice(index, 1);
      }
    }

    config.IsLocked = config.Errors.length > 0;
  }

  private _clearDeductions(payline: Payline, config: PayrollConfig, pay: EmployeePay) {
    for (let d of config.HoldDeductions) {
      const deduction = payline.Deductions.find(x => x.DeductionCode == d);

      if (!deduction) {
        continue;
      }

      deduction.TemporaryAmount = deduction.TemporaryRate = 0;
      this._setUncalculatedPay(pay, true);
    }
  }

  private _clearEarnings(payline: Payline, config: PayrollConfig, pay: EmployeePay) {
    for (let e of config.HoldEarnings) {
      const earning = payline.Earnings.find(x => x.EarningId == e);

      if (!earning) {
        continue;
      }

      earning.TemporaryRateAmount = 0;
      earning.Hours = 0;

      this._setUncalculatedPay(pay, true);
    }
  }

  private _clearTaxes(payline: Payline, config: PayrollConfig, pay: EmployeePay) {
    for (let t of config.HoldTaxes) {
      const tax = payline.Taxes.find(x => x.TaxCode == t.Id);

      if (!tax) {
        continue;
      }

      // if TaxHold.OverridePercentage
      // set tax override calc method to "Fixed Percentage"
      if (t.OverridePercentage) {
        tax.TemporaryCalculationType = tax.CalculationType = 'Percent';
        tax.TemporaryAmount = t.OverridePercentage;
      } else {
        tax.TemporaryCalculationType = tax.CalculationType = 'Fixed';
        tax.TemporaryAmount = 0;
      }

      tax.CurrentAmount = 0;

      this._setUncalculatedPay(pay, true);
    }
  }

  private _getClient(clientCode: string) {
    const key = this._getClientKey(clientCode);
    const client = sessionStorage.getItem(key);

    if (!client) {
      return null;
    }

    const c: Client = JSON.parse(client);
    return c;
  }

  private _getClientKey(client: string) {
    return `client-${client}`;
  }

  private _getEmployeePay(pays: EmployeePay[], personId: number) {
    return pays.find(x => x.PersonId == personId);
  }

  private _getEmployeePayline(pay: EmployeePay, paylineId: number) {
    return pay.Paylines.find(x => x.PaylineId == paylineId);
  }

  private _getPayrollKey(client: string, year: number, seq: number) {
    return `payroll-${client}-${year}-${seq}`;
  }

  private _getSessionData(client: string, year: number, seq: number): PayGridSessionData {
    const key = this._getSessionDataKey(client, year, seq);
    const sessionData = sessionStorage.getItem(key);

    if (!sessionData) {
      return null;
    }

    const d: PayGridSessionData = JSON.parse(sessionData);
    return d;
  }

  private _getSessionDataKey(client: string, year: number, seq: number) {
    return `session-data-${client}-${year}-${seq}`;
  }

  private _getSetupData(client: string, year: number, seq: number): PayrollConfigSetupData {
    const key = this._getSetupDataKey(client, year, seq);
    const setupData = sessionStorage.getItem(key);

    if (!setupData) {
      return null;
    }

    const d: PayrollConfigSetupData = JSON.parse(setupData);
    return d;
  }

  private _getSetupDataKey(client: string, year: number, seq: number) {
    return `setup-data-${client}-${year}-${seq}`;
  }

  // return true if this payline has been calculated
  // at least one time since the payroll was created
  private _hasPaylineEverCalculated(
    payline: Payline,
    pay: EmployeePay,
    payroll: MasterPayrollObject
  ) {
    const sessionData = this._getSessionData(
      payroll.ClientCode,
      payroll.Year,
      payroll.SequenceNumber
    );

    if (sessionData == null) {
      return false;
    }

    return (
      Object.keys(sessionData.CalculatedPaylines).includes(`${pay.PersonId}`) &&
      sessionData.CalculatedPaylines[pay.PersonId].includes(payline.PaylineId)
    );
  }

  private _holdsOverridesApplied(c: PayrollConfig) {
    return (
      c.HoldEarnings.length > 0 ||
      c.HoldDeductions.length > 0 ||
      c.HoldTaxes.length > 0 ||
      c.HoldDirectDeposit ||
      c.PayrollFrequencyOverride ||
      c.HoldTimeOff?.length > 0
    );
  }

  private _initPayline(line: Payline, pay: EmployeePay, payroll: MasterPayrollObject) {
    // payline has not yet been calculated
    // clear presets and unset ok to pay
    // depending on business logic
    this._unsetOkToPay(line, pay, payroll);
    this.clearPresets(line, payroll, pay);

    if ((line.OkToPay && !line.NetAmountPaid) || line.TriggerImmediateCalculation) {
      // task 12486 - only calculate paylines that do
      // not have any totals set. we assume those lines
      // have already been calculated
      this._setUncalculatedPay(pay);
    }
  }

  // do the following data manipulation operations on the payline
  // before the data is built on the grid. if necessary, set the payline
  // to be calculated when it comes in view on the payroll grid.
  // NOTE that this operation runs after the payroll created or fetched from the API.
  // if the payline does not eventually get calculated on the payroll grid, these
  // changes made here will not be persisted in the API cache.
  // ---
  // operations to run:
  // clear presets for holds and overrides
  // uncheck ok to pay based on business logic
  // determine if the line needs to be calculated
  // store payroll in session storage
  private _initPayroll(payroll: MasterPayrollObject) {
    for (let pay of payroll.EmployeePays) {
      for (let line of pay.Paylines) {
        // if the payline has not yet been calculated
        // since payrollw as created, run init operation
        // to fire clear presets
        if (!this._hasPaylineEverCalculated(line, pay, payroll)) {
          this._initPayline(line, pay, payroll);
        }
      }
    }

    const key = this._getPayrollKey(payroll.ClientCode, payroll.Year, payroll.SequenceNumber);
    sessionStorage.setItem(key, JSON.stringify(payroll));

    // bug 19258
    // for any paylines that had presets cleared from holds/overrides
    // or the OkToPay flag was set to false, fire a calc payline now.
    // note that this operation needs to wait until the payroll object
    // is cached in sessionStorage
    for (let pay of payroll.EmployeePays) {
      if (this._paysWithHolds.find(x => x.PersonId == pay.PersonId)) {
        this._paysWithHolds = this._paysWithHolds.filter(x => x.PersonId != pay.PersonId);
        this.calculatePaylines(
          payroll.ClientCode,
          payroll.Year,
          payroll.SequenceNumber,
          pay
        ).subscribe();
      }
    }
  }

  // check if all the data points necessary to run payroll are in session storage
  private _isPayrollDataPopulated(client: string, year: number, seq: number) {
    const c = this._getClient(client);
    const payroll = this.GetPayrollFromCache(client, year, seq);
    const setup = this._getSetupData(client, year, seq);
    const session = this._getSessionData(client, year, seq);

    return c != null && payroll != null && setup != null && session != null;
  }

  private _navigateToManualPayroll(clientCode: string, year: number, seq: number) {
    // navigate to the manual payroll home page.
    this.router.navigate([`/client/${clientCode}/manual-payroll/${year}/${seq}`]);
  }

  private _preSortPaylineByKey(key: number, a: EmployeePay, b: EmployeePay): number {
    if (key === 1) {
      if (this.sortEmployeesByFirstName) {
        return this.getEmployeeFullName(a).toLowerCase() >
          this.getEmployeeFullName(b).toLocaleLowerCase()
          ? 1
          : -1;
      } else {
        return this.getEmployeeLastFirstName(a).toLowerCase() >
          this.getEmployeeLastFirstName(b).toLocaleLowerCase()
          ? 1
          : -1;
      }
    } else if (key === 2) {
      return a.EmployeeNumber - b.EmployeeNumber;
    } else if (key === 3) {
      return a.DivisionId - b.DivisionId;
    } else if (key === 4) {
      return a.DepartmentId - b.DepartmentId;
    }
    return 0;
  }

  private _savePayroll(payroll: MasterPayrollObject) {
    const key = this._getPayrollKey(payroll.ClientCode, payroll.Year, payroll.SequenceNumber);
    payroll.LastEditOn = new Date();
    payroll.LastEditBy = this.authService.getCurrentEmail();

    sessionStorage.setItem(key, JSON.stringify(payroll));
  }

  private _setManualPayLocks(
    payroll: MasterPayrollObject,
    payType: PayTypes,
    config: PayrollStartConfig
  ) {
    // if active payroll is manual and payroll status of 'saved' - everything is enabled
    if (payroll.Status == PayrollStatuses.Saved) {
      config.IsLocked = false;
    } else if (payroll.Status == PayrollStatuses.Started) {
      if (payType == PayTypes.Manual && this.authService.isCurrentUser(payroll.LastEditBy)) {
        config.IsLocked = false;
      } else {
        config.IsLocked = true;
      }
    }
  }

  // update the paygrid session data
  // to track which paylines have been calculated since payroll was created
  private _setPaylineCalculated(
    sessionData: PayGridSessionData,
    payline: Payline,
    pay: EmployeePay
  ) {
    if (!Object.keys(sessionData.CalculatedPaylines).includes(`${pay.PersonId}`)) {
      sessionData.CalculatedPaylines[pay.PersonId] = [];
    }

    if (!sessionData.CalculatedPaylines[pay.PersonId].includes(payline.PaylineId)) {
      sessionData.CalculatedPaylines[pay.PersonId].push(payline.PaylineId);
    }
  }

  private _setPaylinesCalculated(personIds, client: string, year: number, seq: number) {
    // store in session data that these paylines have been calculated
    // in order to properly track when the holds/overrides logic should
    // be run on payroll init
    const sessionData = this._getSessionData(client, year, seq);
    const payroll = this.GetPayrollFromCache(client, year, seq);

    for (let id of personIds) {
      const pay = payroll.EmployeePays.find(x => x.PersonId == id);
      for (let line of pay.Paylines) {
        this._setPaylineCalculated(sessionData, line, pay);
      }
    }

    this.updatePayGridSessionData(client, year, seq, sessionData);
  }

  private _setUncalculatedPay(employeePay: EmployeePay, hasHolds: boolean = false) {
    if (!this.uncalculatedPays.includes(employeePay.PersonId)) {
      this.uncalculatedPays.push(employeePay.PersonId);
    }

    // bug 19258
    // if payline has presets cleared,
    // or OkToPay has been set to false by _unsetOkToPay,
    // calculate the payline right now
    // so totals in the detail editor are correct
    if (hasHolds && !this._paysWithHolds.includes(employeePay)) {
      this._paysWithHolds.push(employeePay);
    }
  }

  private _stitchEmployeePays(payroll: MasterPayrollObject) {
    return this.payrollService
      .getPayrollEmployeePays(payroll.ClientCode, payroll.Year, payroll.SequenceNumber)
      .pipe(
        map(empPays => {
          payroll.EmployeePays = empPays.Model;

          this._trimTaxCodes(payroll);
          return payroll;
        })
      );
  }

  private _trimTaxCodes(payroll: MasterPayrollObject) {
    // bug 9611: strip trailing whitespace from API on tax codes
    // so object lookups can work properly
    payroll.EmployeePays.forEach(pay => {
      pay.Paylines.forEach(line => {
        line.Taxes.map(x => (x.TaxCode = x.TaxCode.trim()));
      });
    });
  }

  // bug 16140. for off cycle payroll, any paylines that are not manual checks should be set as OkToPay = false at payroll create
  // bug 16178 when all earnings are held, set all non-manual check paylines as OkToPay = false at payroll create
  private _unsetOkToPay(line: Payline, pay: EmployeePay, payroll: MasterPayrollObject) {
    if (!line.OkToPay || line.PaylineType == PaylineType.Manual) {
      return;
    }

    const allEarningsHeld =
      this._holdsOverridesApplied(payroll.Config) && this._allEarningsHeld(line, payroll.Config);
    if (payroll.PayType == PayTypes.OffCycle || allEarningsHeld) {
      line.OkToPay = false;

      // payline needs to be calculated in order for change to persist
      this._setUncalculatedPay(pay, true);
    }
  }

  //#endregion
}
